import { AwsRum } from 'aws-rum-web';
import AWS from 'aws-sdk';
import getLog from '../../server/logging/logger';

let awsRum = null;

// Check if we're in dev environment (client-side check via hostname)
const isDevEnvironment = () => {
  if (typeof window === 'undefined') {
    return process.env.NODE_ENV === 'development';
  }
  const { hostname } = window.location;
  return hostname.includes('dev') || hostname.includes('.dev.') || hostname === 'localhost' || hostname === '127.0.0.1';
};

// Create a conditional logger that only logs in dev
const baseLog = getLog('aws-rum');
const log = {
  info: (...args) => {
    if (isDevEnvironment()) {
      baseLog.info(...args);
    }
  },
  warning: (...args) => {
    if (isDevEnvironment()) {
      baseLog.warning(...args);
    }
  },
  error: (...args) => {
    if (isDevEnvironment()) {
      baseLog.error(...args);
    }
  },
};

/* Get consent status from cookie */
const getConsentStatus = () => {
  if (typeof document === 'undefined') {
    return false;
  }

  try {
    const consentCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('bc_consent_bf='));

    if (!consentCookie) {
      return false;
    }

    const consentValue = decodeURIComponent(consentCookie.split('=')[1]);
    const consent = JSON.parse(consentValue);

    return consent && consent.allow && consent.allow.includes(3);
  } catch (error) {
    return false;
  }
};

/* Record a page view in AWS RUM */
export const recordPageView = (pageId = null) => {
  if (awsRum && typeof awsRum.recordPageView === 'function') {
    try {
      // Use provided pageId or default to current pathname
      const pageIdentifier = pageId || (typeof window !== 'undefined' ? window.location.pathname : '/');
      log.info('Recording AWS RUM page view', { pageId: pageIdentifier });
      awsRum.recordPageView(pageIdentifier);
    } catch (error) {
      log.error('Failed to record AWS RUM page view', { error, pageId });
    }
  }
};

/* Initialize AWS RUM once using window config (no polling) */
export const initRUM = async ({ getIdToken }) => {
  if (typeof window === 'undefined' || !window.AWS_RUM_CONFIG) {
    return false;
  }

  const {
    applicationId,
    applicationVersion,
    applicationRegion,
    identityPoolId,
    userPoolId,
  } = window.AWS_RUM_CONFIG;

  if (!applicationId || !identityPoolId || !userPoolId) {
    log.error('Missing required config values, skipping RUM initialization', {
      hasApplicationId: !!applicationId,
      hasIdentityPoolId: !!identityPoolId,
      hasUserPoolId: !!userPoolId,
    });
    return false;
  }

  if (awsRum) {
    log.info('AWS RUM already initialized, skipping');
    return true;
  }

  const idToken = getIdToken();

  if (!idToken) {
    log.warning('No idToken found, skipping RUM initialization');
    return false;
  }

  const hasConsent = getConsentStatus();

  AWS.config.region = applicationRegion;
  const providerName = `cognito-idp.${applicationRegion}.amazonaws.com/${userPoolId}`;

  const credentials = new AWS.CognitoIdentityCredentials({
    IdentityPoolId: identityPoolId,
    Logins: {
      [providerName]: getIdToken(),
    },
  });

  await credentials.getPromise();
  try {
    const rumConfig = {
      identityPoolId,
      endpoint: `https://dataplane.rum.${applicationRegion}.amazonaws.com`,
      telemetries: ['errors', 'performance', 'http'],
      sessionSampleRate: 1,
      allowCookies: hasConsent,
      enableXRay: false,
      signing: true,
      delaySend: true
    };

    awsRum = new AwsRum(applicationId, applicationVersion, applicationRegion, rumConfig);

    awsRum.setAwsCredentials({
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      identityId: credentials.identityId,
    });

    // Record initial page view after a short delay to ensure RUM is ready
    setTimeout(() => {
      recordPageView();
    }, 1000);
  } catch (error) {
    log.error('Failed to initialize AWS RUM', { error });
    return false;
  }

  return true;
};
